import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { groupBy, orderBy, find } from 'lodash';
import { PseudoStates, CardHeader, ListGroupItem, Button } from '@cordata/buffet';
import { all, spread } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, ListGroup, CardColumns } from 'reactstrap';

import { deburrObject } from '~/utils/api/normalizers';

import {
  authorizedPyInstance as py,
  authorizedInstance as rb,
} from '~/utils';

const OnOffSwitch = ({ resource, patientUid }) => {
  const [patientResource, setPatientResource] = useState(resource.patientResource);
  const [isLoading, setIsLoading] = useState(false);
  const [method, setMethod] = useState(resource.patientResource ? 'delete' : 'post');
  const [url, setUrl] = useState(`patient-resources${resource.patientResource ? `/${resource.patientResource.id}` : ''}`);

  const payload = {
    data: {
      patient: { patient_uid: patientUid },
      'reference-resource': { resource_uid: resource.id },
    },
  };

  const handleClick = () => {
    setIsLoading(true);

    rb[method](url, payload)
      .then(r => {
        const notDelete = method !== 'delete';

        setPatientResource(notDelete ? deburrObject(r.data.data) : undefined);
        setMethod(notDelete ? 'delete' : 'post');
        setUrl(`patient-resources${notDelete ? `/${r.data.data.id}` : ''}`);
      }).finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <FontAwesomeIcon icon={isLoading ? 'spinner' : patientResource ? 'check-square' : ['far', 'square']} fixedWidth className="cursor-pointer" onClick={handleClick} spin={isLoading} />
  );
};

OnOffSwitch.propTypes = {
  resource: PropTypes.shape().isRequired,
  patientUid: PropTypes.string,
};

OnOffSwitch.defaultProps = {
  patientUid: undefined,
};

const ResourceDeck = ({
  params: { patient_uid: patientUid },
}) => {
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState({});

  useEffect(() => {
    all([
      py
        .get('/api/data/reference/reference-resource', {
          params: {
            sort: 'sortOrder,category,title',
            resourceTarget: 'patient',
          },
        })
        .then(r => r.data.data.map(deburrObject)),
      py
        .get('/api/data/public/patient-resource', {
          params: {
            patient_uid: patientUid,
          },
        })
        .then(r => r.data.data.map(deburrObject)),
    ]).then(spread((r, p) => {
      setData(groupBy(
        r.map(rr => ({
          ...rr,
          patientResource: find(p, ['resourceUid', rr.id]),
        })),
        c => c.category,
      ));

      setStatus('success');
    }))
      .catch(() => setStatus('error'));
  }, []);

  const handleClick = (rsrc) => {
    if (rsrc.resourceType === 'link') {
      window.open(rsrc.resourceLink, '_blank');
    } else {
      rb
        .get('get-s3-link', { params: { link: rsrc.resourceLink } })
        .then(r => r.data)
        .then(r => window.open(r.link, '_blank'));
    }
  };

  if (status === 'success') {
    return (
      <CardColumns>
        {Object.keys(data).map(category => (
          <Card>
            <CardHeader>
              <h5>{category}</h5>
            </CardHeader>

            <ListGroup flush>
              {orderBy(data[category], ['sortOrder', 'resourceTitle']).map(resource => (
                <ListGroupItem key={resource.id}>
                  <div className="d-flex align-items-center">
                    <div>
                      <OnOffSwitch resource={resource} patientUid={patientUid} />
                    </div>

                    <div className="w-100">
                      <Button className="text-primary text-left" color="text" block onClick={() => handleClick(resource)}>
                        {resource.resourceTitle}
                      </Button>
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
          </Card>
        ))}
      </CardColumns>
    );
  }

  return <PseudoStates status={status} />;
};

ResourceDeck.propTypes = {
  params: PropTypes.shape().isRequired,
};

export default ResourceDeck;
